import PropTypes from 'prop-types';
import { useLayoutEffect, useRef, useState } from 'react';
import {
  finalizeMoneyInput,
  formatMoneyInputDraft,
  parseMoneyInputDraftToCents,
} from '../../domain/finance';

// Keep the caret beside the same digits/comma when grouping inserts or removes dots.
const mapCaret = (rawValue, position, formattedValue) => {
  const prefix = formatMoneyInputDraft(rawValue.slice(0, position)).replace(/\./g, '');
  let remaining = prefix.length;
  let index = 0;
  while (index < formattedValue.length && remaining > 0) {
    if (formattedValue[index] !== '.') remaining -= 1;
    index += 1;
  }
  if (rawValue[position - 1] === '.' && formattedValue[index] === '.') index += 1;
  return index;
};

const MoneyInput = ({
  value = '',
  onChange,
  onFocus,
  onBlur,
  selectOnFocus = true,
  className = '',
  ...inputProps
}) => {
  const inputRef = useRef(null);
  const [selection, setSelection] = useState(null);

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input || !selection || value !== selection.value) return;
    // Also restore when deleting a separator produces the same controlled value.
    input.value = value;
    input.setSelectionRange(selection.start, selection.end, selection.direction);
  }, [value, selection]);

  const emitValue = (rawValue, { finalize = false, caret = null } = {}) => {
    const formattedValue = finalize
      ? finalizeMoneyInput(rawValue)
      : formatMoneyInputDraft(rawValue);
    const amountCents = formattedValue
      ? parseMoneyInputDraftToCents(formattedValue)
      : null;

    setSelection(caret ? {
      value: formattedValue,
      start: mapCaret(rawValue, caret.start, formattedValue),
      end: mapCaret(rawValue, caret.end, formattedValue),
      direction: caret.direction,
    } : null);
    onChange?.(formattedValue, amountCents);
  };

  const handleChange = (event) => {
    const input = event.currentTarget;
    let rawValue = input.value;
    let start = input.selectionStart ?? rawValue.length;
    let end = input.selectionEnd ?? start;
    const inputType = event.nativeEvent.inputType;
    // A thousands separator is decoration: deleting it should not trap the caret.
    if (start === end && value.length === rawValue.length + 1
        && value.replace(/\./g, '') === rawValue.replace(/\./g, '')) {
      if (inputType === 'deleteContentBackward' && start > 0) {
        rawValue = rawValue.slice(0, start - 1) + rawValue.slice(start);
        start -= 1;
        end = start;
      } else if (inputType === 'deleteContentForward') {
        rawValue = rawValue.slice(0, start) + rawValue.slice(start + 1);
      }
    }
    emitValue(rawValue, { caret: { start, end, direction: input.selectionDirection } });
  };

  const handleFocus = (event) => {
    if (selectOnFocus && event.currentTarget.value) {
      event.currentTarget.select();
    }
    onFocus?.(event);
  };

  const handleBlur = (event) => {
    if (value) emitValue(value, { finalize: true });
    onBlur?.(event);
  };

  const handlePaste = (event) => {
    const pastedValue = event.clipboardData?.getData('text');
    if (typeof pastedValue !== 'string') return;

    if (event.currentTarget.readOnly || event.currentTarget.disabled) return;
    event.preventDefault();
    const input = event.currentTarget;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? input.value.length;
    // Parse after insertion: pasted centavos such as "05" must keep their leading zero.
    const rawValue = input.value.slice(0, start) + pastedValue + input.value.slice(end);
    const position = start + pastedValue.length;
    emitValue(rawValue, {
      finalize: true,
      caret: { start: position, end: position, direction: 'none' },
    });
  };

  return (
    <input
      {...inputProps}
      ref={inputRef}
      className={className}
      type="text"
      inputMode="decimal"
      pattern="[0-9.,]*"
      autoComplete="off"
      value={value}
      onChange={handleChange}
      onPaste={handlePaste}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};

MoneyInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  selectOnFocus: PropTypes.bool,
  className: PropTypes.string,
};

export default MoneyInput;